import {
  CommunicationThreadBody,
  useCommunicationThreadTitle,
} from "@/components/communications/CommunicationThreadBody.tsx";
import { CanvasModalCard } from "./CanvasModalCard.tsx";

/**
 * The canvas overlay for a conversation target: every message in an email or
 * text exchange, in the same {@link CanvasModalCard} shell the contact, source
 * and artifact overlays use.
 *
 * Renders the shared {@link CommunicationThreadBody} the right sidebar renders,
 * so nothing is given up by opening here rather than there. What is gained is
 * where it opens: a conversation reached from a contact's activity list used to
 * push the sidebar out beside the whole screen, which put a second detail
 * surface on a screen that already had one — and left the contact card that
 * produced it sitting behind a panel with a separate way back.
 *
 * The caller resolves the id from the canvas URN. Any one message's source id
 * addresses the whole thread; the server expands it.
 */
export const CanvasCommunicationThreadModal = ({
  communicationId,
  container,
  showBack,
  isFullscreen,
  onBack,
  onClose,
}: {
  /** Source ent id of any message in the conversation, or undefined when none is targeted. */
  communicationId: string | undefined;
  /** The stack layer this modal portals into. */
  container: HTMLElement;
  /** Show the leading Back affordance (this layer sits above the stack root). */
  showBack: boolean;
  /** Whether the presented entity is shown fullscreen. */
  isFullscreen: boolean;
  /** Pop this layer. */
  onBack: () => void;
  /** Clear the whole stack. */
  onClose: () => void;
}) => {
  // Title only; the body owns its own copy of the same query (deduped).
  const title = useCommunicationThreadTitle(communicationId);

  // No conversation targeted (query disabled): render no card.
  if (communicationId === undefined) return null;

  return (
    <CanvasModalCard
      title={title}
      container={container}
      showBack={showBack}
      isFullscreen={isFullscreen}
      onBack={onBack}
      onClose={onClose}
    >
      <div className="px-4 py-4">
        <CommunicationThreadBody communicationId={communicationId} />
      </div>
    </CanvasModalCard>
  );
};
